import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useScrapEyeMetadata } from "contexts/scrapEyeMetadata";
import {
  EventLabellingModelStatus,
  ReviewSlotRevision,
} from "src/store/api/generatedApi";
import { VideoControlProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { TimelineZoomProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/TimelineZoomContext";
import { VideoSuspensionProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoSuspensionContext";
import {
  eventDiffersFromBaseline,
  ManualReviewEventProvider,
  ScrapEyesEventWithReviewData,
  useManualReviewEvents,
} from "./contexts/ManualReviewEventContext";
import { MassDataProvider } from "./contexts/MassDataContext";
import { TimelineExclusionProviders } from "./TimelineExclusionProviders";
import { TimelineExclusionReport } from "./hooks/useTimelineExclusions";
import { useReviewEventsWithExclusions } from "./hooks/useReviewEventsWithExclusions";
import {
  exclusionPeriodIdsFromDecisions,
  LabellerExclusionDecisions,
} from "./utils/timelineExclusions";
import { LinkedPeriodLabellingSurface } from "./LinkedPeriodLabellingSurface";
import type { BringInPeriodRequest } from "./LinkedPeriodBringInDialog";
import { focusSeedAdjustments } from "./utils/editScope";
import { massEventsFromReviewEvents } from "./utils/eventUtils";

export interface CapturedEvents {
  reviewSlotRevisions?: ReviewSlotRevision[];
  allEvents: ScrapEyesEventWithReviewData[];
  originalEventsById: Map<string, ScrapEyesEventWithReviewData>;
  seedEventsById: Map<string, ScrapEyesEventWithReviewData>;
}

// Lifts the modal-local edited events out of its own event provider so the confirm
// handler can return the target period's complete set to the save.
const CaptureLocalEvents = ({
  captureRef,
}: {
  captureRef: React.RefObject<CapturedEvents | null>;
}) => {
  const { allEvents, originalEventsById, seedEventsById, reviewSlotRevisions } =
    useManualReviewEvents();
  captureRef.current = {
    allEvents,
    originalEventsById,
    seedEventsById,
    reviewSlotRevisions,
  };
  // The surface unmounts whenever the merge it captured stops standing — an exclusion
  // re-key, or an error after a successful load. Drop the capture with it, so a caller
  // cannot confirm a merge that is no longer on screen.
  useEffect(
    () => () => {
      captureRef.current = null;
    },
    [captureRef]
  );
  return null;
};

// The neighbour lies outside the loaded window, so fetch its events into a local
// event/mass context and edit them there before bringing them into the save.
export const FetchedNeighbourPlayer = ({
  location,
  request,
  captureRef,
  labellerExclusions,
  omittedModelStatuses,
  onReadyChange,
  getHighlightColor,
}: {
  location: string;
  request: BringInPeriodRequest;
  captureRef: React.RefObject<CapturedEvents | null>;
  labellerExclusions: LabellerExclusionDecisions;
  omittedModelStatuses: readonly EventLabellingModelStatus[];
  onReadyChange: (isReady: boolean) => void;
  // A pure pass-through; forwarded unchanged to this component's own editing surface.
  getHighlightColor?: (
    event: ScrapEyesEventWithReviewData
  ) => string | null | undefined;
}) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const { extra_data: extraData, reservation } = useScrapEyeMetadata();
  const hasScaleWeights = extraData.includes("mass_data");

  // The focus window overlaps the page's in-view window on the in-view side, so
  // seed the modal with the page's current edits there rather than the re-fetched
  // originals: edited events overlapping the focus window override the fetch by id,
  // and events the page has deleted are dropped from it. Read the page events
  // through refs and recompute only when the hop's window changes, so a page
  // re-fetch behind the open modal can't re-seed and discard edits made here.
  const {
    allEvents: pageEvents,
    originalEventsById: pageOriginalEventsById,
    modifiedEventIds: pageModifiedEventIds,
  } = useManualReviewEvents();
  const pageEventsRef = useRef(pageEvents);
  pageEventsRef.current = pageEvents;
  const pageOriginalEventsByIdRef = useRef(pageOriginalEventsById);
  pageOriginalEventsByIdRef.current = pageOriginalEventsById;
  const pageModifiedEventIdsRef = useRef(pageModifiedEventIds);
  pageModifiedEventIdsRef.current = pageModifiedEventIds;
  // Edits from periods already brought in this chain overlap this hop's focus too, so
  // they seed it alongside the page's edits — a brought-in edit wins over the page copy
  // of the same id, and a period stepped back to reopens with its own edits.
  const { focusStart, focusEnd, carriedEvents, carriedOriginalEvents } =
    request;
  const { seedOverrides, omittedOriginalEventIds } = useMemo(() => {
    const carriedOriginalsById = new Map(
      (carriedOriginalEvents ?? []).map((event) => [event.id, event])
    );
    const editedEventIds = new Set([
      ...pageModifiedEventIdsRef.current,
      ...(carriedEvents ?? [])
        .filter((event) =>
          eventDiffersFromBaseline(event, carriedOriginalsById.get(event.id))
        )
        .map((event) => event.id),
    ]);
    return focusSeedAdjustments({
      pageEvents: pageEventsRef.current,
      carriedEvents: carriedEvents ?? [],
      pageOriginalEvents: [...pageOriginalEventsByIdRef.current.values()],
      carriedOriginalEvents: carriedOriginalEvents ?? [],
      editedEventIds,
      focusStart,
      focusEnd,
    });
  }, [focusStart, focusEnd, carriedEvents, carriedOriginalEvents]);

  // The page's decisions, mapped onto this window's own labelling periods. Inheriting them
  // rather than re-deriving the defaults keeps the same labellers out of the flank the two
  // surfaces share — including a labeller the reviewer hid by hand, and one they put back in
  // view — and still covers the target period, whose periods the page never reported.
  const resolveInheritedExclusions = useCallback(
    (report: TimelineExclusionReport) =>
      exclusionPeriodIdsFromDecisions({
        labellerTimelines: report.labeller_timelines,
        decisions: labellerExclusions,
      }),
    [labellerExclusions]
  );

  const { resolvedResponse, supersededResponse, exclusions, isError, refetch } =
    useReviewEventsWithExclusions({
      tenantName,
      location,
      windowStart: focusStart,
      windowEnd: focusEnd,
      resolveExclusions: resolveInheritedExclusions,
      // This surface's event ids have to line up with the page's across the flank they
      // share, so every labeller feeding its merge must be resolvable — including the
      // chunk ending on the focus start, which the window's own report cannot see.
      probePaddingSeconds: reservation.chunk_seconds,
      omittedModelStatuses,
    });

  // Toggling a labeller re-merges this window. Leave the merge it had on screen while the new
  // one is fetched, with the timelines marked as loading, rather than emptying the modal — the
  // controls only unlock when there is nothing unsaved to lose, and readiness falls either way,
  // so the reviewer cannot confirm the merge being replaced.
  const focusEventsResponse = resolvedResponse ?? supersededResponse;
  const isRemerging =
    resolvedResponse === undefined && focusEventsResponse !== undefined;
  const massEvents = useMemo(
    () => massEventsFromReviewEvents(focusEventsResponse?.review_events ?? []),
    [focusEventsResponse]
  );

  const isLoaded = resolvedResponse !== undefined;
  // Reported as a value, not a one-shot signal: an exclusion re-key sends this surface back to
  // a fetch, and a caller latching on a single "ready" would act on a merge that has been
  // superseded.
  useEffect(() => {
    onReadyChange(isLoaded);
  }, [isLoaded, onReadyChange]);

  if (isError) {
    return (
      <Stack spacing={2} alignItems="center" py={4}>
        <Typography color="error">{t("bringInPeriodLoadError")}</Typography>
        <Button variant="outlined" onClick={refetch}>
          {t("retry")}
        </Button>
      </Stack>
    );
  }

  if (!focusEventsResponse) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    // The page suspends its own tiles for the length of the chain; this surface's player
    // is the one the reviewer is looking at, so it opts back in.
    <VideoSuspensionProvider suspended={false}>
      <VideoControlProvider>
        <TimelineZoomProvider
          fullDuration={focusEnd.diff(focusStart, "second")}
          videoStart={focusStart}
        >
          <ManualReviewEventProvider
            events={focusEventsResponse.review_events}
            reviewSlotRevisions={focusEventsResponse.review_slot_revisions}
            location={location}
            videoStart={focusStart}
            windowStart={focusStart}
            windowEnd={focusEnd}
            seedOverrides={seedOverrides}
            omittedOriginalEventIds={omittedOriginalEventIds}
          >
            <MassDataProvider
              windowStart={focusStart}
              windowEnd={focusEnd}
              location={location}
              enabled={hasScaleWeights}
            >
              <TimelineExclusionProviders
                exclusions={exclusions}
                locked={
                  seedOverrides !== undefined ||
                  omittedOriginalEventIds !== undefined
                }
              >
                <LinkedPeriodLabellingSurface
                  location={location}
                  start={focusStart}
                  end={focusEnd}
                  windowStart={request.windowStart}
                  windowEnd={request.windowEnd}
                  reviewPeriods={request.reviewPeriods}
                  labellerTimelines={focusEventsResponse.labeller_timelines}
                  railcarSlotMaterials={
                    isRemerging
                      ? undefined
                      : focusEventsResponse.railcar_slot_materials
                  }
                  massEvents={massEvents}
                  bringInWillVerifyPeriodIds={request.willVerifyPeriodIds ?? []}
                  isLoading={isRemerging}
                  getHighlightColor={getHighlightColor}
                />
              </TimelineExclusionProviders>
              <CaptureLocalEvents captureRef={captureRef} />
            </MassDataProvider>
          </ManualReviewEventProvider>
        </TimelineZoomProvider>
      </VideoControlProvider>
    </VideoSuspensionProvider>
  );
};
